/** Desk owns protest/posting operations; Results setup owns their configuration. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "@/api/client";
import { createDeskApi, saveDeskDownload } from "@/api/desk";
import type { DeskClient, DeskContext, DeskState, Posting, SignRole } from "@/api/desk";
import { useAuthStore } from "@/stores/auth";
import { Dialog } from "@/components/competition/Layout";
import { useOnline } from "@/components/competition/state";
import {
  PostingForm,
  FileProtestForm,
  CorrectionForm,
  DecisionForm,
} from "@/components/desk/Forms";
import {
  deskActions,
  canOpenFiling,
  deadlineLabel,
  kindName,
  money,
  safeAttachment,
  timestamp,
} from "@/components/desk/policy";
import "./Desk.css";

type OpenDialog =
  | { kind: "post"; postingId?: string }
  | { kind: "file" }
  | { kind: "sign"; postingId: string; role: SignRole }
  | { kind: "panel" | "correct" | "snapshot"; postingId: string }
  | { kind: "decide"; protestId: string };
const deskRoles = ["admin", "chief_judge", "head_judge", "chief_scorer"];
const errorText = (cause: unknown) => {
  const code = cause instanceof ApiError ? cause.code : undefined;
  const descriptions: Record<string, string> = {
    version_conflict: "Results changed. Review the refreshed version before trying again.",
    posting_not_current: "A newer posting exists. Review its latest version.",
    correction_window_closed:
      "The correction window has closed. A chief judge can override it with a reason.",
    protest_window_closed: "The protest window has closed.",
    filer_not_eligible: "This filer is not eligible under the competition rule.",
    unanimous_panel:
      "This rule does not accept a protest when every judge placed the heat identically.",
    pending_scores: "This scope still has pending or unreleased scores.",
    pdf_pending: "The PDF is still rendering. Refresh and try again.",
    posting_before_score_change:
      "Posting time must be at or after the last score change. Leave it blank to use the server time.",
    posting_in_future: "Posting time cannot be in the future.",
    required_signatures_below_rule: "This rule requires more signatures. Review the signer count.",
    official_posting: "This scope is already official. Open a correction on its latest posting.",
    official_results_required: "This rule requires an official posting first.",
    correction_already_open: "A correction is already open. Review the refreshed posting.",
    correction_not_open: "Open a correction before posting a corrected version.",
    protest_kind_not_allowed: "This kind of filing is not allowed by the posting's rule.",
    protests_not_allowed: "Protests are not allowed under the current rule.",
    attempt_not_found:
      "This attempt is no longer available. Refresh and select the current attempt.",
    competitor_not_found: "This competitor is no longer in the heat. Refresh the rider list.",
    occurrence_unknown:
      "The attempt end time is not recorded. An official must verify the attempt before filing.",
    series_not_available:
      "This rule needs a series counter, which is not available for this competition.",
  };
  const detail =
    cause instanceof ApiError && cause.detail && typeof cause.detail === "object"
      ? (cause.detail as Record<string, unknown>)
      : null;
  const rule = typeof detail?.rule === "string" ? ` ${detail.rule}` : "";
  return (
    (code && descriptions[code]
      ? descriptions[code]
      : cause instanceof Error
        ? cause.message
        : "Desk could not be loaded. Try again.") + rule
  );
};
function scopeName(state: DeskState, p: Posting) {
  const scope = state.eligible_scopes.find((s) => s.division_id === p.division_id);
  return `${scope?.name ?? "Division"} · ${p.heat_id ? (scope?.heats.find((h) => h.id === p.heat_id)?.name ?? "Heat") : "Whole division"}`;
}
function currentPosting(state: DeskState, p: Posting) {
  return !state.postings.some(
    (other) =>
      other.division_id === p.division_id &&
      other.heat_id === p.heat_id &&
      other.posting_no > p.posting_no,
  );
}

export function DeskPage({ client: provided }: { client?: DeskClient } = {}) {
  const { competitionId } = useParams<{ competitionId: string }>();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const client = useMemo(
    () => provided ?? createDeskApi(competitionId ?? ""),
    [provided, competitionId],
  );
  const [context, setContext] = useState<DeskContext | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setContext(null);
    setError("");
    client
      .context(userId)
      .then((value) => {
        if (active) setContext(value);
      })
      .catch((cause) => {
        if (active) setError(errorText(cause));
      });
    return () => {
      active = false;
    };
  }, [client, userId, revision]);
  if (!context)
    return (
      <section className="desk-page" aria-busy={!error}>
        <h1>Desk</h1>
        {error ? (
          <div className="desk-warning" role="alert">
            <p>{error}</p>
            <button className="desk-primary" onClick={() => setRevision((n) => n + 1)}>
              Retry
            </button>
          </div>
        ) : (
          <p role="status">Loading competition permissions…</p>
        )}
      </section>
    );
  return (
    <DeskWorkspace
      key={competitionId ?? context.competition.id}
      context={context}
      client={client}
    />
  );
}

export function DeskWorkspace({ context, client }: { context: DeskContext; client: DeskClient }) {
  const online = useOnline();
  const allowed = context.roles.some((r) => deskRoles.includes(r));
  const enabled = !!context.competition.features.protests && !!context.competition.protest;
  const [state, setState] = useState<DeskState | null>(null);
  const [loading, setLoading] = useState(true);
  const [fresh, setFresh] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [writeError, setWriteError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<"protests" | "postings">("protests");
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const [now, setNow] = useState(Date.now());
  const [updated, setUpdated] = useState<string | null>(null);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const loadNumber = useRef(0);
  const submission = useRef<{ body: string; key: string } | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const refresh = useCallback(async () => {
    if (!allowed || !enabled || !navigator.onLine) return;
    const number = ++loadNumber.current;
    setLoading(true);
    try {
      const value = await client.state();
      if (alive.current && number === loadNumber.current) {
        setState(value);
        setFresh(true);
        setLoadError("");
        setUpdated(new Date().toISOString());
      }
    } catch (cause) {
      if (alive.current && number === loadNumber.current) {
        setLoadError(errorText(cause));
        setFresh(false);
      }
    } finally {
      if (alive.current && number === loadNumber.current) setLoading(false);
    }
  }, [client, allowed, enabled]);
  useEffect(() => {
    if (online) void refresh();
    else setFresh(false);
  }, [online, refresh]);
  useEffect(() => {
    if (!allowed || !enabled) return;
    const off = client.subscribe?.(() => void refresh());
    const poll = setInterval(() => {
      if (navigator.onLine) void refresh();
    }, 30000);
    return () => {
      off?.();
      clearInterval(poll);
    };
  }, [client, allowed, enabled, refresh]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const baseActions = deskActions(context.roles, null, online, fresh && !busy);
  const open = (value: OpenDialog) => {
    submission.current = null;
    setWriteError("");
    setNotice("");
    setDialog(value);
  };
  const close = () => {
    if (!busy) {
      setDialog(null);
      setWriteError("");
    }
  };
  const key = (body: unknown) => {
    const value = JSON.stringify(body);
    if (submission.current?.body !== value)
      submission.current = { body: value, key: crypto.randomUUID() };
    return submission.current.key;
  };
  const mutate = async (action: () => Promise<unknown>, success: string) => {
    if (!online || !fresh || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setWriteError("");
    try {
      await action();
      if (alive.current) {
        setDialog(null);
        setNotice(success);
        await refresh();
      }
    } catch (cause) {
      if (alive.current) {
        setWriteError(errorText(cause));
        if (cause instanceof ApiError && cause.status === 409) await refresh();
      }
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const download = async (action: () => Promise<Blob>, filename: string) => {
    if (!online || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setWriteError("");
    try {
      const blob = await action();
      if (!alive.current) return;
      saveDeskDownload(blob, filename);
      setNotice(
        filename.endsWith(".pdf")
          ? "Downloaded. Open the PDF to print it."
          : "Protest CSV downloaded.",
      );
    } catch (cause) {
      if (alive.current) setWriteError(errorText(cause));
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const selected =
    dialog && "postingId" in dialog
      ? state?.postings.find((p) => p.id === dialog.postingId)
      : undefined;
  const selectedProtest =
    dialog?.kind === "decide" ? state?.protests.find((p) => p.id === dialog.protestId) : undefined;
  const selectedActions = deskActions(
    context.roles,
    selected ?? null,
    online,
    fresh && !busy,
    selected && state ? currentPosting(state, selected) : false,
  );
  const modalTitle = dialog
    ? {
        post:
          dialog.kind === "post" && dialog.postingId ? "Post corrected results" : "Post results",
        file: "File a protest",
        sign: "Sign results",
        panel: "Approve panel",
        correct: "Open a correction",
        snapshot: "Frozen result snapshot",
        decide: "Decide protest",
      }[dialog.kind]
    : "";
  if (!allowed)
    return (
      <section className="desk-page">
        <h1>Desk</h1>
        <p role="alert">
          Desk access requires an administrator, head judge, chief judge or chief scorer.
        </p>
      </section>
    );
  if (!enabled)
    return (
      <section className="desk-page">
        <h1>Desk</h1>
        <p>The Protests module is off. An administrator can enable it in Setup → Results.</p>
      </section>
    );
  const openCount = state?.protests.filter((p) => p.status === "open").length ?? 0;
  return (
    <section className="desk-page" aria-label="Competition desk">
      <div className="desk-content" inert={!!dialog}>
        <header className="desk-header">
          <div>
            <p className="desk-eyebrow">{context.competition.name}</p>
            <h1>Desk</h1>
            <p className="desk-intro">Protests, result postings and official sign-off.</p>
            <p className="desk-note">Times shown in {context.competition.timezone}.</p>
          </div>
          <button
            className="desk-secondary"
            disabled={!online || loading || busy}
            onClick={() => void refresh()}
          >
            Refresh
          </button>
        </header>
        {!online && (
          <p className="desk-warning" role="status">
            Offline — read only. Changes need a connection. Nothing is queued.
          </p>
        )}
        {loadError && (
          <div className="desk-warning" role="alert">
            <strong>{state ? "Could not refresh Desk" : "Desk could not be loaded"}</strong>
            <p>
              {loadError}
              {state
                ? " Showing the last loaded records. Writes are unavailable until refreshed."
                : ""}
            </p>
            <button
              disabled={!online || loading || busy}
              className="desk-secondary"
              onClick={() => void refresh()}
            >
              Try again
            </button>
          </div>
        )}
        {notice && (
          <p className="desk-success" role="status">
            {notice}
          </p>
        )}
        {writeError && !dialog && (
          <p className="desk-warning" role="alert">
            {writeError}
          </p>
        )}
        <div
          className="desk-tabs"
          role="tablist"
          aria-label="Desk sections"
          onKeyDown={(e) => {
            const order = ["protests", "postings"] as const;
            const index = tab === "protests" ? 0 : 1;
            const next =
              e.key === "ArrowRight" || e.key === "ArrowLeft"
                ? order[1 - index]
                : e.key === "Home"
                  ? order[0]
                  : e.key === "End"
                    ? order[1]
                    : null;
            if (next) {
              e.preventDefault();
              setTab(next);
              e.currentTarget.querySelector<HTMLButtonElement>(`#desk-tab-${next}`)?.focus();
            }
          }}
        >
          {(["protests", "postings"] as const).map((value) => (
            <button
              id={`desk-tab-${value}`}
              key={value}
              role="tab"
              aria-selected={tab === value}
              aria-controls={`desk-panel-${value}`}
              tabIndex={tab === value ? 0 : -1}
              onClick={() => setTab(value)}
            >
              {value === "protests" ? "Protests" : "Postings"}
            </button>
          ))}
        </div>
        {!state && !loadError && (
          <p role="status" className="desk-empty" aria-busy="true">
            Loading Desk records…
          </p>
        )}
        {state && (
          <div
            role="tabpanel"
            id={`desk-panel-${tab}`}
            aria-labelledby={`desk-tab-${tab}`}
            tabIndex={0}
          >
            <div className="desk-toolbar">
              <div>
                <h2>
                  {tab === "protests"
                    ? `${openCount} open protest${openCount === 1 ? "" : "s"}`
                    : "Result postings"}
                </h2>
                <p>
                  {tab === "protests"
                    ? "Every filing and decision stays in the record."
                    : "Frozen versions keep the result history visible."}
                </p>
              </div>
              <div className="desk-actions">
                {tab === "protests" ? (
                  <>
                    <button
                      className="desk-secondary"
                      disabled={!online || busy}
                      onClick={() => void download(() => client.csv(), "protests.csv")}
                    >
                      Export CSV
                    </button>
                    <button
                      className="desk-primary"
                      disabled={
                        !baseActions.file || !canOpenFiling(state, context.competition.protest)
                      }
                      onClick={() => open({ kind: "file" })}
                    >
                      File protest
                    </button>
                  </>
                ) : (
                  <button
                    className="desk-primary"
                    disabled={!baseActions.post}
                    onClick={() => open({ kind: "post" })}
                  >
                    Post results
                  </button>
                )}
              </div>
            </div>
            {tab === "protests" ? (
              state.protests.length ? (
                <ol className="desk-records">
                  {[...state.protests]
                    .sort(
                      (a, b) =>
                        Number(b.status === "open") - Number(a.status === "open") ||
                        Date.parse(b.filed_at) - Date.parse(a.filed_at),
                    )
                    .map((p) => {
                      const scope = state.eligible_scopes.find((s) =>
                        s.heats.some((h) => h.id === p.heat_id),
                      );
                      const heat = scope?.heats.find((h) => h.id === p.heat_id);
                      const subject = state.postings
                        .flatMap((post) => post.snapshot?.heats ?? [])
                        .flatMap((h) => h.results.competitors)
                        .find((r) => r.heat_rider_id === p.heat_rider_id)?.name;

                      return (
                        <li className="desk-record" key={p.id}>
                          <div className="desk-record-top">
                            <div>
                              <p className="desk-eyebrow">{kindName[p.kind]}</p>
                              <h3>
                                {subject ??
                                  (p.heat_rider_id ? "Competitor filing" : "Heat-wide filing")}
                              </h3>
                              <p className="desk-note">Filed by {p.filed_by_name}</p>
                              <p>
                                {scope ? `${scope.name} · ` : ""}
                                {heat?.name ?? "Retained heat record"}
                                {p.attempt_number ? ` · Attempt ${p.attempt_number}` : ""}
                              </p>
                            </div>
                            <span
                              className={`desk-chip ${p.status === "open" ? "desk-chip-open" : ""}`}
                            >
                              {p.status === "open" ? deadlineLabel(p.deadline_at, now) : p.status}
                            </span>
                          </div>
                          {p.deadline_at && (
                            <p className="desk-note">
                              Deadline {timestamp(p.deadline_at, context.competition.timezone)}
                            </p>
                          )}
                          <p className="desk-grounds">{p.grounds}</p>
                          <div className="desk-meta">
                            <span>
                              {money(p.fee_cents, p.currency)} ·{" "}
                              {p.fee_paid ? "Paid" : "Not marked paid"}
                            </span>
                            {p.free_protest_used && (
                              <span className="desk-chip">Free protest used</span>
                            )}
                            <span>Filed {timestamp(p.filed_at, context.competition.timezone)}</span>
                          </div>
                          {p.attachments.length > 0 && (
                            <ul className="desk-attachments" aria-label="Protest attachments">
                              {p.attachments.map((link, index) => {
                                const safe = safeAttachment(link);
                                return (
                                  <li key={`${index}:${link}`}>
                                    {safe ? (
                                      <a href={safe} target="_blank" rel="noopener noreferrer">
                                        Attachment {index + 1} ↗
                                      </a>
                                    ) : (
                                      <span>Attachment {index + 1} — unavailable link</span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          {p.decision && (
                            <div className="desk-decision">
                              <strong>Decision · {p.status}</strong>
                              <p>{p.decision}</p>
                              <p className="desk-note">
                                {timestamp(p.decided_at, context.competition.timezone)}
                              </p>
                            </div>
                          )}
                          {p.status === "open" &&
                            context.roles.some((r) => ["admin", "chief_judge"].includes(r)) && (
                              <div className="desk-record-actions">
                                <button
                                  className="desk-primary"
                                  disabled={!baseActions.decide}
                                  onClick={() => open({ kind: "decide", protestId: p.id })}
                                >
                                  Decide protest
                                </button>
                              </div>
                            )}
                        </li>
                      );
                    })}
                </ol>
              ) : (
                <div className="desk-empty">
                  <h3>No protests filed</h3>
                  <p>
                    File on behalf of an eligible rider or team representative when a review is
                    needed.
                  </p>
                </div>
              )
            ) : state.postings.length ? (
              <ol className="desk-records">
                {[...state.postings]
                  .sort(
                    (a, b) =>
                      Date.parse(b.posted_at) - Date.parse(a.posted_at) ||
                      b.posting_no - a.posting_no,
                  )
                  .map((p) => {
                    const current = currentPosting(state, p);
                    const authority = deskActions(
                      context.roles,
                      p,
                      online,
                      fresh && !busy,
                      current,
                    );
                    const rights = deskActions(context.roles, p, true, true, current);
                    return (
                      <li key={p.id} className={`desk-record ${current ? "" : "desk-history"}`}>
                        <div className="desk-record-top">
                          <div>
                            <p className="desk-eyebrow">{scopeName(state, p)}</p>
                            <h3>
                              v{p.posting_no} ·{" "}
                              {p.kind === "corrected"
                                ? "Corrected results"
                                : p.official
                                  ? "Official results"
                                  : "Provisional results"}
                            </h3>
                            <p>Posted {timestamp(p.posted_at, context.competition.timezone)}</p>
                          </div>
                          <span
                            className={`desk-chip ${p.official ? "desk-chip-official" : "desk-chip-open"}`}
                          >
                            {!current
                              ? "Historical"
                              : p.correction_opened_at
                                ? "Correction open"
                                : p.official
                                  ? "Official"
                                  : "Provisional"}
                          </span>
                        </div>
                        {p.supersedes_id && (
                          <p className="desk-note">
                            v{p.posting_no} supersedes v
                            {state.postings.find((old) => old.id === p.supersedes_id)?.posting_no ??
                              "previous"}
                            . Earlier results remain in history.
                          </p>
                        )}
                        <dl className="desk-signatures">
                          <div>
                            <dt>
                              {p.snapshot?.pack === "iwwf_cable_2026"
                                ? "Head judge"
                                : "Chief judge"}
                            </dt>
                            <dd>
                              {p.signed_chief_judge_at
                                ? `Signed ${timestamp(p.signed_chief_judge_at, context.competition.timezone)}`
                                : "Unsigned"}
                            </dd>
                          </div>
                          <div>
                            <dt>Chief scorer</dt>
                            <dd>
                              {p.signed_chief_scorer_at
                                ? `Signed ${timestamp(p.signed_chief_scorer_at, context.competition.timezone)}`
                                : "Unsigned"}
                            </dd>
                          </div>
                          <div>
                            <dt>Required</dt>
                            <dd>
                              {p.required_signatures} signature
                              {p.required_signatures === 1 ? "" : "s"}
                              {p.snapshot?.panel_required
                                ? ` · panel ${p.panel_approved_at ? "approved" : "pending"}`
                                : ""}
                            </dd>
                          </div>
                        </dl>
                        <p className="desk-note">
                          {p.cut_line ? `Cut line ${p.cut_line} · ` : ""}
                          {deadlineLabel(p.protest_deadline_at, now)}
                        </p>
                        {p.protest_deadline_at && (
                          <p className="desk-note">
                            Deadline{" "}
                            {timestamp(p.protest_deadline_at, context.competition.timezone)}
                          </p>
                        )}
                        <div className="desk-record-actions">
                          <button
                            className="desk-secondary"
                            onClick={() => open({ kind: "snapshot", postingId: p.id })}
                          >
                            View snapshot
                          </button>
                          <button
                            className="desk-secondary"
                            disabled={!online || busy || !p.pdf_ready}
                            onClick={() =>
                              void download(() => client.pdf(p.id), `results-v${p.posting_no}.pdf`)
                            }
                          >
                            {p.pdf_ready ? "Print PDF" : "PDF rendering…"}
                          </button>
                          {rights.panel && p.snapshot?.panel_required && (
                            <button
                              className="desk-primary"
                              disabled={!authority.panel}
                              onClick={() => open({ kind: "panel", postingId: p.id })}
                            >
                              Approve panel
                            </button>
                          )}
                          {!p.official && rights.signChiefJudge && (
                            <button
                              className="desk-secondary"
                              disabled={!authority.signChiefJudge}
                              onClick={() =>
                                open({ kind: "sign", postingId: p.id, role: "chief_judge" })
                              }
                            >
                              Sign as chief judge
                            </button>
                          )}
                          {!p.official && rights.signChiefScorer && (
                            <button
                              className="desk-secondary"
                              disabled={!authority.signChiefScorer}
                              onClick={() =>
                                open({ kind: "sign", postingId: p.id, role: "chief_scorer" })
                              }
                            >
                              Sign as chief scorer
                            </button>
                          )}
                          {rights.correct && (
                            <button
                              className="desk-secondary"
                              disabled={!authority.correct}
                              onClick={() => open({ kind: "correct", postingId: p.id })}
                            >
                              Open correction
                            </button>
                          )}
                          {current &&
                            p.correction_opened_at &&
                            context.roles.some((r) => deskRoles.includes(r)) && (
                              <button
                                className="desk-primary"
                                disabled={!baseActions.post}
                                onClick={() => open({ kind: "post", postingId: p.id })}
                              >
                                Post corrected version
                              </button>
                            )}
                        </div>
                      </li>
                    );
                  })}
              </ol>
            ) : (
              <div className="desk-empty">
                <h3>No result postings</h3>
                <p>Post a frozen result snapshot once scoring is complete and released.</p>
              </div>
            )}
          </div>
        )}
        <footer className="desk-footer">
          <span>{online ? (fresh ? "Connected" : "Refresh needed") : "Offline"}</span>
          <span>
            {updated
              ? `Last loaded ${timestamp(updated, context.competition.timezone)}`
              : "No records loaded yet"}
          </span>
        </footer>
      </div>
      {dialog && state && (
        <Dialog title={modalTitle} onClose={close} className="desk-dialog">
          <div className="desk-modal-heading">
            <p className="desk-note">
              {context.competition.name}
              {selected ? ` · ${scopeName(state, selected)} · v${selected.posting_no}` : ""}
              {selectedProtest
                ? ` · ${state.eligible_scopes.flatMap((s) => s.heats).find((h) => h.id === selectedProtest.heat_id)?.name ?? "Retained heat record"}`
                : ""}
            </p>
            <button
              type="button"
              className="desk-secondary"
              disabled={busy}
              aria-label="Close dialog"
              onClick={close}
            >
              ×
            </button>
          </div>
          {!online && (
            <p className="desk-warning" role="status">
              Offline — this form cannot be submitted. Nothing is queued.
            </p>
          )}
          {writeError && (
            <p className="desk-warning" role="alert">
              {writeError}
            </p>
          )}
          {dialog.kind === "post" && (
            <PostingForm
              state={state}
              context={context}
              corrected={selected}
              disabled={!baseActions.post}
              submit={(body) =>
                void mutate(
                  () => client.post(body, key(["post", body])),
                  "Results snapshot posted.",
                )
              }
            />
          )}
          {dialog.kind === "file" && (
            <FileProtestForm
              state={state}
              context={context}
              client={client}
              disabled={!baseActions.file}
              submit={(body) =>
                void mutate(
                  () => client.file(body, key(["file", body])),
                  "Protest filed. The record shows the actual fee and free-protest usage.",
                )
              }
            />
          )}
          {dialog.kind === "correct" && selected && (
            <CorrectionForm
              posting={selected}
              context={context}
              disabled={!selectedActions.correct}
              submit={(reason, override) => {
                const body = { reason, override, expected_version: selected.version };
                void mutate(
                  () => client.correct(selected.id, body, key(["correct", selected.id, body])),
                  "Correction opened. Edit the scores, then post the corrected version.",
                );
              }}
            />
          )}
          {dialog.kind === "decide" && selectedProtest && (
            <DecisionForm
              protest={selectedProtest}
              disabled={!baseActions.decide || selectedProtest.status !== "open"}
              submit={(body) =>
                void mutate(() => client.decide(selectedProtest.id, body), "Decision recorded.")
              }
            />
          )}
          {dialog.kind === "sign" && selected && (
            <div className="desk-form">
              <p>
                {scopeName(state, selected)} · v{selected.posting_no}
              </p>
              <p>
                Your signature confirms this frozen posting as{" "}
                {dialog.role === "chief_judge" ? "chief judge" : "chief scorer"}. Existing result
                data will not be recomputed.
              </p>
              <button
                className="desk-primary"
                disabled={
                  dialog.role === "chief_judge"
                    ? !selectedActions.signChiefJudge
                    : !selectedActions.signChiefScorer
                }
                onClick={() =>
                  void mutate(
                    () =>
                      client.sign(
                        selected.id,
                        dialog.role,
                        selected.version,
                        key(["sign", selected.id, dialog.role, selected.version]),
                      ),
                    "Signature recorded.",
                  )
                }
              >
                Confirm signature
              </button>
            </div>
          )}
          {dialog.kind === "panel" && selected && (
            <div className="desk-form">
              <p>
                Confirm that the panel approves {scopeName(state, selected)}, v{selected.posting_no}
                .
              </p>
              {selected.snapshot?.pack === "iwwf_cable_2026" && (
                <p>This records the heat head judge's approval and makes the posting official.</p>
              )}
              <button
                className="desk-primary"
                disabled={!selectedActions.panel}
                onClick={() =>
                  void mutate(
                    () =>
                      client.approve(
                        selected.id,
                        selected.version,
                        key(["panel", selected.id, selected.version]),
                      ),
                    "Panel approval recorded.",
                  )
                }
              >
                Confirm panel approval
              </button>
            </div>
          )}
          {dialog.kind === "snapshot" && selected && (
            <div className="desk-snapshot">
              <p>
                Posted {timestamp(selected.posted_at, context.competition.timezone)}. This snapshot
                stays unchanged when scores are corrected.
              </p>
              {selected.snapshot?.heats.map((heat) => (
                <section key={heat.heat_id}>
                  <h3>{heat.heat_name}</h3>
                  <table>
                    <caption>Frozen results · v{selected.posting_no}</caption>
                    <thead>
                      <tr>
                        <th>Place</th>
                        <th>Athlete</th>
                        <th>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {heat.results.competitors.map((r) => (
                        <tr key={r.heat_rider_id}>
                          <td>{r.placement ?? "—"}</td>
                          <th scope="row">
                            {r.name}
                            {r.attempts.some((a) => a.under_protest) ? " *" : ""}
                          </th>
                          <td>{r.status_label ?? r.final ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              ))}
            </div>
          )}
          <button
            type="button"
            className="desk-secondary desk-cancel"
            disabled={busy}
            onClick={close}
          >
            {dialog.kind === "snapshot" ? "Close snapshot" : "Cancel"}
          </button>
        </Dialog>
      )}
    </section>
  );
}
